import {NotificationEntry} from '../../../components/notification/notification.model';
import {ILayer} from '../../../shared/map/layer/layer.type';
import {KonegisCauseCode, BundledCauseCode} from './konegis-cause-code';
import {AlarmMessage} from '../../../alarm/alarm-message';
import {IFlag} from '../../../shared/flag/flag.model';

export interface BaseTrain {
  arrStation: string;
  arrTime: Date;
  category: string; // S, IC, AE, etc...
  commercialId: string;
  createdAt: Date;
  depStation: string;
  depTime: Date;
  state: 'RUNNING' | 'CANCELLED' | 'WAITING' | 'ARRIVED';
  status: 'success' | 'warning' | 'danger' | 'cancelled';
  tripDate: string;
  tripNumber: number;
  type: TrainType
  /**
   * Is considered a VAK train if it has a VAK wagon
   */
  _id: string;
  messages: NotificationEntry[];
  /**
   * key = station
   */
  causes: Map<string, BundledCauseCode>;
  alarms: AlarmMessage[];
  flags: IFlag[];
  flagged: boolean;
  layer: ILayer;
  vak: boolean;
}

export interface RunningTrain extends BaseTrain {
  bearing: number;
  calculatedDelta: number;
  equipment: string;
  label: string;
  positionChanged: Date;
  receivedDelta: number;
  station: string;
  subTitle: string;
  title: string;
  velocity: number;
  x: number;
  y: number;
  timetable?: TrainTimetableEntry[];
}

export interface EquipmentTrain extends BaseTrain, TrainMarker {
  machineId: string;
  title: string;
}

export interface ScheduleTrain extends BaseTrain {
  arrived: Date | false;
  ready: boolean;
  confirmed: boolean;
  timetable: TrainTimetableEntry[];
  causeString?: string;
}


export interface TrainHistoryEntry {
  calculatedDelta: number;
  category: string;
  createdAt: Date;
  state: string;
  station: string;
  timetable: HistoryTimetableEntry[];
  tripDate: string;
  tripNumber: string;
  type: string;
  velocity: number;
  x: number;
  y: number;

}

export interface HistoryTimetableEntry {
  causes: KonegisCauseCode;
  station: string;
}


/*
export interface Train {
  _id: string;
  type: 'LONGDISTANCE' | 'COMMUTER' | 'FREIGHT' | 'SHUNTING' | 'LOCOMOTIVE' | 'OTHER';
  velocity: number;
  bearing: number;
  receivedDelta: number;
  commercialId: string;
  equipment: string;
  x: number;
  y: number;
  category: string; // S, IC, AE, etc...
  depStation: string;
  arrStation: string;
  eventCode: string;
  station: string;
  tripDate: string;
  tripNumber: number;
  status: 'success' | 'warning' | 'danger' | 'cancelled';
  calculatedDelta: number;
  positionChanged: Date;
  arrived: Date | false;
  state: 'RUNNING' | 'CANCELLED' | 'WAITING' | 'ARRIVED';
  ready: boolean;
  depTime: Date;
  arrTime: Date;
  createdAt: Date;
  label: string;
  title: string;
  subTitle: string;
  timetable: TrainTimetableEntry[];
  arrival: TrainTimetableEntry;
  departure: TrainTimetableEntry;
  composition: any;
  messages: NotificationEntry[];
  causes: Map<string, string[]>; // key = station, value = Human readable explanations of LIVI causes
  alarms: AlarmMessage[];
  flags: IFlag[];
  flagged: boolean;
  layer: Layer;
  causeString?: string;
}
*/

/**
 * Dates come from backend as strings and need to be parsed first.
 * @see parseToDate in Utils.ts
 *
 */

export interface TrainTimetableEntry {
  actual: Date | string;
  cancelled: boolean;
  causes: KonegisCauseCode;
  commercial: boolean;
  diff: string;
  forecast: Date;
  schedule: Date | string;
  station: string;
  stop: boolean;
  track: string;
  type: 'ARRIVAL' | 'DEPARTURE';
}

export interface Vehicle {
  /**
   * Type of wagon
   * eg. IM1, Edo, Edb, ERd, Edfs, Ed ...
   */
  type: string;
  /**
   * Wagon number,
   * eg. 7103
   */
  vehicleNumber?: string;
  /**
   * Sale number
   * eg. 6
   */
  bookingNumber?: string | number; // not sure on the actual implementation yet so both cases need to be considered
}

export interface TrainEquipment {
  /**
   * Locomotive number of hauling equipment
   */
  locId?: string; // 7752
  /**
   * Rough type of equipment (SR1,SM3,MVO,MVR, ...)
   */
  equipmentType: string;
  /**
   * Data on wagons of a wagon group
   */
  vehicles?: Vehicle[];
}

export interface Person {
  /**
   * Person number
   * eg. 123456
   */
  personId: string;
  /**
   * Workshift identifier
   * eg. 12345
   */
  shiftId: string;
  /**
   * Workshift date
   */
  shiftDate: string;
}

export interface TrainSubdependency {
  /**
   * Dependencies of hauling equipment according to Loko-plan/-guidance
   */
  locs?: TrainEquipment[];
  /**
   * Dependencies of motor and long-distance wagons according to Kapu-plan
   */
  compositions?: TrainEquipment[];
  /**
   * Personnel dependencies according to VIP
   */
  personnel?: Person[];
}


/**
 * Updated to accommodate https://miproagile.atlassian.net/browse/VGR-399
 */
export interface TrainDependency {
  /**
   * Not in Mylly spec but included in provided examples from Avarko
   */
  created: Date;
  /**
   * Should come once Mylly is updated week 10/2020
   * Not in Mylly spec but included in provided examples from Avarko
   */
  updated?: Date;
  depBeginDateTime: Date;
  dependentLocs: string[];
  /**
   * Will stay as-is in the upcoming update week 10/2020
   */
  dependent_compositions: string[];
  location: string;
  operatingDate: string;
  trainNumber: string;
  /**
   * Will come after Mylly update week 10/2020
   */
  subDependencies?: TrainSubdependency;
}

export interface TrainMarker extends RunningTrain {
  markerContainerClass: string;
  markerIconClass: string;
  markerTitleClass: string;
  direction: number;
}

export interface TrainComposition {
  _id: string;
  compositions: JourneySection[];
}

export interface TrainTracking {
  trackSection: string;
  station: string;
  type: 'TRAIN-TRACKING';
  trainId: string;
  timestamp: string;
}

export interface JourneySection {
  beginTimeTableRow: any;
  endTimeTableRow: any;
  locomotives: CompositionLocomotive[];
  wagons: CompositionWagon[];
}

export interface CompositionLocomotive {
  location: number;
  locomotiveType: string;
  powerType: string;
}

export interface CompositionWagon {
  wagonType: string;
  location: number;
  salesNumber: number;
  length: number;
  pet?: boolean;
  disabled?: boolean;
  catering?: boolean;
}

export interface CargoManifest {
  H: WagonRow[];
  JLP_LITTERA: string;
  JLP_LKM: string;
  JLP_SUURINJUNANOPEUS: string;
  JLP_SUURINJUNAPAINO: string;
  JLP_SUURINJUNAPITUUS: string;
  JLP_VETURIEN_PITUUS: string;
}

export interface WagonFields {
  KAL_AKSELILUKU: string;
  KAL_JARRU_PAINO: string;
  KAL_KALNRO: string;
  KAL_KALUSTOSARJA: string;
  KAL_KANTAVUUS: string;
  KAL_KYKLAJI: string;
  KAL_KYTILA: string;
  KAL_LITTERA: string;
  KAL_PITUUS: string;
  KAL_RAJ_LASKUMAESSA: string;
  KAL_RAJ_RAIDEJARRUSSA: string;
  KAL_TAARA: string;
  KAL_TILAVUUS: string;
  MAA_MAALYHENNE: string;
  MAA_MAAKOODI: string;
}

export interface WagonRow extends WagonFields {
  E: WagonInfo;
  I: ShipmentInfo[];
}

export interface WagonInfo {
  KAL_TOIMIIKO_JARRU: string;
  KAL_VLV_KISKOJARRU_ARVO: string;
  OSK_BRUTTO: string;
  OSK_JATKOJUNANUMERO: string;
  OSK_JATKOJUNA_PVM: string;
  OSK_JATKOJUNA_VAUNURYHMA: string;
  OSK_KULJETUSLAATU: string;
  OSK_KUMULAKM: string;
  OSK_LAHTOASEMA: string;
  OSK_MAARAASEMA: string;
  OSK_YLIMHUOM: string;
  OSK_SELOSTE: string;
  VLV_JARRU_PAINO: string;
}

export interface ShipmentInfo {
  J: CargoInfo[];
  KTI_ALKUP_LAHTOMAA: string;
  KTI_ASIAKAS_VIITE: string;
  KTI_KULJETUSLAJI: string;
  KTI_LAHETTAJA_NIMI: string;
  KTI_LAHETTAJA_OSOITE: string;
  KTI_LAHETTAJA_POSTINRO: string;
  KTI_LAHETTAJA_POSTITP: string;
  KTI_LAHETTAJA_TUNNUS: string;
  KTI_LAHETYSNUMERO: string;
  KTI_LAHETYS_BRUTTO: string;
  KTI_LAHTOASEMA: string;
  KTI_LOP_MAARAASEMA: string;
  KTI_LOP_MAARAMAA: string;
  KTI_MAARAASEMA: string;
  KTI_MAKSAJA_NIMI: string;
  KTI_MAKSAJA_TUNNUS: string;
  KTI_MUUTOS: string;
  KTI_RAHTIKIRJANUMERO: string;
  KTI_VAK_KOODISTO: string;
  KTI_VASTOTTAJA_NIMI: string;
  KTI_VASTOTTAJA_POSTITP: string;
  KTI_VASTOTTAJA_POSTINRO: string;
  KTI_RAJANYLITYS_LKPNRO: string;
  KTI_RAJANYLITYS_PAIKKA: string;
}

export interface CargoInfo {
  KTI_RAHTIKIRJANUMERO: string;
  KTR_KOLLILKM: string;
  KTR_KONTIN_TAARA: string;
  KTR_TAVARANIMI: string;
  KTR_TAVARAN_TODPAINO: string;
  KTR_TAVARAN_VRPAINO: string;
  KTR_VAARA_TUNNUS: string;
  KTR_VAK_KOODISTO: string;
  KTR_VAK_TEKNKUVAUS: string;
  KTR_YK_TUNNUS: string;
  KTR_VAK_LISATIETO: string;
  KTR_TERMINAALI: string;
  KTR_LAIVA: string;
  KTR_LAIVAN_LAHTOPVM: string;
  KTR_VIIM_PURK_AIKA: string;
}

export interface CargoRow {
  cargo: CargoInfo[];
  wagonData: WagonFields;
  wagonInfo: WagonInfo;
  KAL_KALNRO: string;
  KAL_LITTERA: string;
  KTI_LAHETTAJA_NIMI: string;
  KTI_LAHETTAJA_OSOITE: string;
  KTI_LAHETTAJA_POSTINRO: string;
  KTI_LAHETTAJA_POSTITP: string;
  KTI_LAHETTAJA_TUNNUS: string;
  KTI_VASTOTTAJA_NIMI: string;
  KTI_VASTOTTAJA_POSTITP: string;
  KTI_VASTOTTAJA_POSTINRO: string;
  KTI_LAHTOASEMA: string;
  KTI_LOP_MAARAASEMA: string;
  KTI_LOP_MAARAMAA: string;
  KTI_MAARAASEMA: string;
  KTI_RAHTIKIRJANUMERO: string;
  MAA_MAAKOODI: string;
  OSK_SELOSTE: string;
}

export interface TrainShift {
  trainNumber: string;
  operatingDate: String;
  messageCreationDateTime: Date;
  /**
   * Shifts for the personnel of the train
   */
  shifts: Shift[];
}
export interface PersonnelShift {
  personId: string; // "123456"
  minDate: string;  // "2020-02-18"
  maxDate: string;
  messageCreationDateTime: Date;
  shifts: Shift[];
}
export type ShiftPersonGroup = 'DRIVER' | 'CONDUCTOR';
export enum RoleGroupName {
  Driver = 'VEK',
  AssistDriver = 'AVEK',
  Conductor = 'KOND',
  AssistConductor = 'AKOND',
  Package = 'PAKETTI',
}
export interface Shift {
  shiftId: string;
  date: string;
  personGroup: ShiftPersonGroup;
  personId: string;
  tasks: Task[];
}
export type TaskRole = 'LEAD' | 'GUIDE' | 'ASSIST' | 'TRAINEE' | 'PACKAGE' | 'CAR_PACKAGE' | 'CAR_PACKAGE_DRIVER';
export interface Task {
  role?: TaskRole;
  taskId?: string;
  shortName?: string;
  comment?: string;
  trainNumber?: string;
  operatingDate?: string;
  fromStation: string;
  toStation: string;
  taskStartDateTime: Date;
  taskEndDateTime: Date;
  preparationMinutes: number;
  wrapUpMinutes: number;
}

export type TrainCategory = string;
export type TrainType = 'LONGDISTANCE' | 'COMMUTER' | 'FREIGHT' | 'SHUNTING' | 'LOCOMOTIVE' | 'OTHER';